import React from "react";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis } from "recharts";
import { ClipboardText, CaretRight, CurrencyDollar, Wrench } from "@phosphor-icons/react";
import { CcPanel, Delta, Empty } from "./CcPanel";
import { C, aud, audShort, num, timeAgo } from "./ccUtils";

const TONE = { neutral: "#d4d4d4", amber: C.amber, sky: C.sky, green: C.green, red: C.red };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function PipelinePanel({ war }) {
  return (
    <CcPanel title="Warranty pipeline · live from Monday.com" icon={ClipboardText} testid="panel-warranty-pipeline" className="h-[146px]" delay={0.05}
      aside={<span data-testid="pipeline-last-sync">Synced {timeAgo(war.last_sync)}</span>}>
      <div className="flex items-stretch gap-1.5 h-full">
        {war.pipeline.map((s, i) => (
          <React.Fragment key={s.group}>
            {i > 0 && (s.tone === "red" ? <div className="w-px bg-neutral-800 mx-1.5 shrink-0" /> : <CaretRight size={12} className="self-center text-neutral-700 shrink-0" />)}
            <div data-testid={`pipeline-stage-${slug(s.label)}`} className="flex-1 min-w-0 rounded-lg bg-[#171717] border border-neutral-800 px-2.5 py-2 flex flex-col justify-between"
              style={{ borderBottomColor: TONE[s.tone], borderBottomWidth: 2 }}>
              <span className="text-[10px] uppercase tracking-wide leading-tight text-neutral-400">{s.label}</span>
              <span className="font-heading text-[28px] font-bold tabular-nums leading-none" style={{ color: s.n ? TONE[s.tone] : C.muted }}>{num(s.n)}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </CcPanel>
  );
}

export function ValueTrendPanel({ war }) {
  const last = war.value_trend.length - 1;
  return (
    <CcPanel title="Approved value · 6 months" icon={CurrencyDollar} testid="panel-value-trend" bodyClass="flex flex-col" delay={0.1}
      aside={<Delta v={war.avg_cost_30d_change_pct} label="avg claim" testid="value-trend-delta" />}>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={war.value_trend} margin={{ top: 22, right: 4, left: 4, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fill: "#737373", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#262626" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {war.value_trend.map((m, i) => <Cell key={m.month} fill={C.gold} fillOpacity={i === last ? 0.45 : 0.9} />)}
              <LabelList dataKey="value" position="top" formatter={audShort} style={{ fill: C.gold, fontSize: 11, fontFamily: "JetBrains Mono" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-neutral-600 mt-1">Faded bar = month to date · by claim date</p>
    </CcPanel>
  );
}

export function FailuresPanel({ war }) {
  return (
    <CcPanel title="Top faults · 90 days" icon={Wrench} testid="panel-top-failures" delay={0.15}>
      {war.top_failures.length ? (
        <div className="space-y-1.5">
          {war.top_failures.map((f, i) => (
            <div key={`${f.component}-${i}`} data-testid={`failure-row-${i}`} className="flex items-center gap-2.5 rounded-lg bg-[#171717] border border-neutral-800/70 px-2.5 py-1.5">
              <span className="w-5 h-5 rounded-md bg-neutral-800 text-[10px] font-mono text-neutral-400 flex items-center justify-center shrink-0">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-neutral-100 truncate flex items-center gap-1.5">
                  {f.component}
                  {f.safety && <span className="text-[9px] font-semibold uppercase tracking-wider text-red-400 border border-red-500/40 bg-red-500/10 rounded px-1">Safety</span>}
                </div>
                <div className="text-[10px] text-neutral-500 truncate">{f.system}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-mono text-white tabular-nums">{f.n} reports</div>
                <div className="text-[10px] font-mono" style={{ color: C.gold }}>avg {aud(f.avg_cost)}</div>
              </div>
            </div>
          ))}
        </div>
      ) : <Empty>No faults recorded in the last 90 days</Empty>}
    </CcPanel>
  );
}
